import React from "react";
import { Link } from "react-router-dom";
import { getCourseLink, getModuleLink } from "../../../../utils/LinkedProgrammeUtils";
import {
    StyledIssueArrow,
    StyledIssueCode,
    StyledIssueContent,
    StyledIssueDescription,
    StyledIssueDivider,
    StyledIssueIcon,
    StyledIssueMain,
    StyledIssueModuleName,
    StyledIssueRow,
    StyledMismatchTag,
    StyledMismatchTags,
    StyledPopoverIssues,
} from "./ModuleReadinessPopover.styles";

/** Icon and tone per Matchstatus; the statuses are documented on PmcManager's `_resolveModuleMatch`. */
const MATCH_STATUS_TONE = {
    0: { tone: "destructive", icon: "times-circle" },
    3: { tone: "muted", icon: "minus-circle" },
    4: { tone: "info", icon: "info-circle" },
};
const DEFAULT_MATCH_STATUS_TONE = { tone: "warning", icon: "exclamation-circle" };

/**
 * Module-readiness problem rows shown inside ModuleReadinessPopover. A row's name links to its
 * module, or to its course for a course with no module in the version.
 * @param {Array<object>} problems Readiness problems to render.
 */
function ModuleReadinessIssuesList({ problems }) {
    return (
        <StyledPopoverIssues>
            {problems.map((problem, index) => {
                const missingVersionModule = problem.matchStatus === 3;
                // Per design: status 4 rows show only the linked code, status 3 rows only the module code
                const showModuleCode = problem.matchStatus !== 4 && problem.moduleCode;
                const showLinkedCode = !missingVersionModule && problem.linkedCode;
                const nameLink = getModuleLink(problem.moduleId) || getCourseLink(problem.linkedCourseId);
                const courseLink = getCourseLink(problem.linkedCourseId);
                const mismatchTags = [
                    problem.mismatchIntake,
                    problem.mismatchCampus,
                    problem.mismatchType,
                ].filter(Boolean);

                const { tone, icon } =
                    MATCH_STATUS_TONE[problem.matchStatus] || DEFAULT_MATCH_STATUS_TONE;

                return (
                    <StyledIssueRow
                        key={`${problem.moduleCode}-${problem.linkedCode}-${index}`}
                    >
                        <StyledIssueIcon
                            $tone={tone}
                            className={`fas fa-${icon}`}
                            aria-hidden="true"
                        />

                        <StyledIssueContent>
                            <StyledIssueMain>
                                <StyledIssueModuleName as={nameLink ? Link : "span"} to={nameLink}>
                                    {problem.moduleName || "Module"}
                                </StyledIssueModuleName>

                                {showModuleCode && (
                                    <>
                                        <StyledIssueDivider />
                                        <StyledIssueCode
                                            as={nameLink ? Link : "span"}
                                            to={nameLink}
                                            $destructive={missingVersionModule}
                                        >
                                            {problem.moduleCode}
                                        </StyledIssueCode>
                                    </>
                                )}

                                {showLinkedCode && (
                                    <>
                                        <StyledIssueArrow>→</StyledIssueArrow>
                                        <StyledIssueCode
                                            as={courseLink ? Link : "span"}
                                            to={courseLink}
                                        >
                                            {problem.linkedCode}
                                        </StyledIssueCode>
                                    </>
                                )}
                            </StyledIssueMain>

                            {mismatchTags.length > 0 && (
                                <StyledMismatchTags>
                                    {mismatchTags.map((mismatchValue) => (
                                        <StyledMismatchTag key={mismatchValue}>
                                            {mismatchValue}
                                            <i className="fas fa-not-equal" aria-hidden="true" />
                                        </StyledMismatchTag>
                                    ))}
                                </StyledMismatchTags>
                            )}

                            {problem.explanation && (
                                <StyledIssueDescription>
                                    {problem.explanation}
                                </StyledIssueDescription>
                            )}
                        </StyledIssueContent>
                    </StyledIssueRow>
                );
            })}
        </StyledPopoverIssues>
    );
}

export default ModuleReadinessIssuesList;
